import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import type { EmailOtpType } from "@supabase/supabase-js";
import { AuthLayout } from "@/components/AuthLayout";
import { Button } from "@/components/ui/Button";
import { FormMessage, Spinner } from "@/components/ui/Feedback";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/lib/supabase";

/**
 * Where social sign-in lands, plus the emailed links that have no form behind
 * them.
 *
 * ── What must never come back ───────────────────────────────────────────────
 *
 * Supabase's default templates point at `{{ .ConfirmationURL }}`, which is
 * `https://<project>.supabase.co/auth/v1/verify?token=...&redirect_to=...` - a
 * **single-use GET on somebody else's domain**. Mail security scanners fetch
 * every URL in an incoming message, so GoTrue burns the token before the human
 * clicks and they get `otp_expired` on a link a minute old. No template in
 * supabase/templates/ links there, and none should.
 *
 * ── Invite and recovery are NOT handled here ────────────────────────────────
 *
 * They used to be, and redeeming them on arrival was a weaker version of the
 * same bug: this page consumed the token on load, so anything that *renders* a
 * link rather than merely fetching it still destroyed the invitation.
 *
 * Those two now go straight to `/accept-invite` and `/reset-password`, which
 * render a password form first and redeem the token when it is **submitted** -
 * see `ResetPassword.tsx`. That is the only arrangement where a GET, from any
 * client, changes nothing.
 *
 * What is left here is the arrivals with no form to defer to:
 *
 * `?token_hash=&type=` signup and email_change - confirmations with nothing for
 *                      the user to fill in, so redeeming on load is the whole
 *                      interaction and there is no earlier moment to protect.
 * `?code=`             a social sign-in. The SDK exchanges it via
 *                      `detectSessionInUrl`; this page finishes the exchange
 *                      itself in the cases where the SDK declines to - see
 *                      `completeCodeExchange` below.
 * `#error=` `?error=`  GoTrue refusing before it ever got here.
 *
 * ── This page's job is to say what went wrong ───────────────────────────────
 *
 * It used to report every failure as "we couldn't confirm that link - it may
 * have expired", including social sign-ins that had no link and had not
 * expired, because the only failure it could see was its own eight-second
 * timeout. A wrong cause is worse than no cause: it sends the reader to retry
 * the one thing that was never the problem. Everything below exists to replace
 * that guess with what the provider actually said.
 */

/** Where each kind of emailed link goes once its token has been redeemed. */
const DESTINATION: Record<string, string> = {
  signup: "/onboarding",
  magiclink: "/app",
  email_change: "/app",
};

/**
 * Types this page must **not** redeem, and where their token belongs instead.
 *
 * Current templates link straight to these screens, so this only fires for mail
 * sent before that change - but those links live in inboxes for a week, and
 * breaking them would look exactly like the bug this whole design removes.
 */
const DEFERRED: Record<string, string> = {
  invite: "/accept-invite",
  recovery: "/reset-password",
};

/** What went wrong, in GoTrue's own terms. */
type Failure = { code: string; description: string | null };

type Copy = { title: string; body: string };

/** An emailed confirmation that could not be redeemed, keyed by its `type`. */
const LINK_COPY: Record<string, Copy> = {
  signup: {
    title: "We couldn't confirm that address",
    body: "The confirmation may have expired, or it may already have been used. Signing in again will send a new code.",
  },
  default: {
    title: "We couldn't confirm that link",
    body: "It may have expired, or it may already have been used.",
  },
};

/** A social sign-in that came back without a session and without a reason. */
const SOCIAL_DEFAULT: Copy = {
  title: "We couldn't finish signing you in",
  body: "The provider sent you back, but the sign-in did not complete. Try again, or use your email address and password.",
};

const PKCE_BODY =
  "This browser could not prove it was the one that started the sign-in, which happens when the attempt was left open too long or its half was cleared. Start it again from the sign-in screen.";

const FAILURE_COPY: Record<string, Copy> = {
  provider_email_needs_verification: {
    title: "That account has no confirmed email address",
    body: "Your provider did not confirm the address on that account, so it cannot be used to sign in here. Use your email address and password, or another provider.",
  },
  provider_disabled: {
    title: "That sign-in method is not available here",
    body: "It has not been turned on for this workspace yet. Use your email address and password.",
  },
  signup_disabled: {
    title: "Sign-up is turned off here",
    body: "Ask the person who set up your workspace to invite you.",
  },
  access_denied: {
    title: "Sign-in was declined",
    body: "The request was turned down before it finished - either by you, or by the account's own administrator.",
  },
  bad_oauth_state: { title: "This sign-in could not be completed", body: PKCE_BODY },
  bad_code_verifier: { title: "This sign-in could not be completed", body: PKCE_BODY },
  flow_state_not_found: { title: "This sign-in could not be completed", body: PKCE_BODY },
  flow_state_expired: { title: "This sign-in took too long", body: PKCE_BODY },
  validation_failed: { title: "This sign-in could not be completed", body: PKCE_BODY },
};

const NO_PROVIDER_EMAIL = "Error getting user email from external provider";

const NO_PROVIDER_EMAIL_COPY: Copy = {
  title: "Your provider sent no email address",
  body: "The sign-in itself worked, but the account came back without an email address, and TaskIQ identifies people by theirs. Use your email address and password, or ask whoever manages your Microsoft account to publish it.",
};

function copyFor(failure: Failure, linkType: string | null): Copy {
  if (failure.description?.includes(NO_PROVIDER_EMAIL)) return NO_PROVIDER_EMAIL_COPY;

  const known = FAILURE_COPY[failure.code];
  if (known) return known;

  // An emailed confirmation and a social sign-in fail in different words: one
  // has a link that may have been spent, the other has no link at all.
  if (linkType === null) return SOCIAL_DEFAULT;
  return LINK_COPY[linkType] ?? LINK_COPY.default;
}

export function AuthCallback() {
  const { status } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const tokenHash = params.get("token_hash");
  const type = params.get("type") ?? "";
  const next = safeNext(params.get("next"));

  const [failure, setFailure] = React.useState<Failure | null>(readUrlFailure);

  React.useEffect(() => {
    if (failure) clearUrlFailure();
  }, [failure]);

  /* ── Emailed link: redeem the hash ──────────────────────────────────── */
  React.useEffect(() => {
    if (!tokenHash) return;

    // Invitations and password resets sent before the templates were changed
    // still point here. Hand the token on **unredeemed** rather than spending
    // it: the destination screen consumes it on submit, which is the whole
    // point. Without this branch every link already sitting in an inbox breaks.
    if (DEFERRED[type]) {
      navigate(`${DEFERRED[type]}?token_hash=${encodeURIComponent(tokenHash)}`, { replace: true });
      return;
    }

    let cancelled = false;

    void supabase.auth
      .verifyOtp({ token_hash: tokenHash, type: type as EmailOtpType })
      .then(({ error }) => {
        if (cancelled) return;
        if (error) {
          setFailure({ code: error.code ?? "link_invalid", description: error.message });
          return;
        }
        // Replace, so the back button does not return to a URL whose token is
        // now spent and would render the failure screen.
        navigate(next ?? DESTINATION[type] ?? "/app", { replace: true });
      });

    return () => {
      cancelled = true;
    };
  }, [tokenHash, type, next, navigate]);

  /* ── Social sign-in: finish the exchange, or find out why not ───────── */
  React.useEffect(() => {
    if (tokenHash || failure) return;

    let cancelled = false;
    void completeCodeExchange().then((error) => {
      if (!cancelled && error) setFailure(error);
    });

    return () => {
      cancelled = true;
    };
  }, [tokenHash, failure]);

  /* ── Social sign-in: the session has resolved ───────────────────────── */
  React.useEffect(() => {
    if (tokenHash || failure) return;
    if (status === "signed-out") return;
    if (
      status === "ready" ||
      status === "pending" ||
      status === "error" ||
      status === "onboarding"
    ) {
      // RequireAuth and the onboarding guard sort out which of those the user
      // can actually use. This route only has to stop being in the way.
      navigate("/app", { replace: true });
    }
  }, [status, tokenHash, failure, navigate]);

  React.useEffect(() => {
    if (tokenHash) return;
    // A social sign-in that has resolved to nothing after eight seconds is not
    // going to. Leaving a spinner up forever is the worst available answer -
    // but this is now the last resort rather than the first, and it reports
    // only what it actually knows.
    const timer = setTimeout(() => {
      if (status === "signed-out")
        setFailure((prev) => prev ?? { code: "timeout", description: null });
    }, 8000);
    return () => clearTimeout(timer);
  }, [status, tokenHash]);

  if (failure) {
    const copy = copyFor(failure, tokenHash ? type : null);

    return (
      <AuthLayout title={copy.title} subtitle={copy.body}>
        {/* `auth-stack`, as on every other screen in this shell that puts more
            than one thing in the body. Without it the children are two bare
            block elements and the button sits flush against the text above it. */}
        <div className="auth-stack">
          {/* The provider's own words, kept rather than swallowed. They are the
              only thing that separates one of these failures from another, and
              a screenshot carrying them is the difference between a support
              thread and a fix. */}
          {failure.description && failure.description !== copy.body && (
            <FormMessage tone="info">{failure.description}</FormMessage>
          )}
          <Link to="/login">
            <Button>Go to sign in</Button>
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="One moment" subtitle="Confirming your link.">
      <Spinner className="spinner--lg" />
    </AuthLayout>
  );
}

function readUrlFailure(): Failure | null {
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const query = new URLSearchParams(window.location.search);
  const pick = (key: string) => hash.get(key) ?? query.get(key);

  const error = pick("error");
  const code = pick("error_code");
  const description = pick("error_description");
  if (!error && !code && !description) return null;

  return { code: code ?? error ?? "unspecified_error", description };
}

function clearUrlFailure(): void {
  const url = new URL(window.location.href);
  for (const key of ["error", "error_code", "error_description"]) url.searchParams.delete(key);
  url.hash = "";
  window.history.replaceState(window.history.state, "", url.toString());
}

async function completeCodeExchange(): Promise<Failure | null> {
  const { data } = await supabase.auth.getSession();
  if (data.session) return null;

  // Still here, so the SDK did not consume it: on success it deletes `code`
  // from the URL itself.
  const code = new URLSearchParams(window.location.search).get("code");
  if (!code) return null;

  const { error } = await supabase.auth.exchangeCodeForSession(code);
  if (!error) return null;
  return { code: error.code ?? "exchange_failed", description: error.message };
}

/**
 * Only same-origin paths, and only ones starting with a single slash.
 *
 * `next` comes out of an email template, which means it is attacker-influencable
 * for anyone who can trigger a mail to an address they control. Without this,
 * `?next=https://evil.example` turns the confirmation flow into an open redirect
 * carrying a freshly minted session. `//evil.example` is rejected for the same
 * reason: browsers read it as protocol-relative and it is a URL, not a path.
 */
function safeNext(value: string | null): string | null {
  if (!value) return null;
  if (!value.startsWith("/") || value.startsWith("//")) return null;
  return value;
}
